import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminBreadcrumbs } from "@/components/admin/AdminBreadcrumbs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ProfissionalDashboard } from "@/components/admin/ProfissionalDashboard";
import { ProfissionalDadosPessoais } from "@/components/admin/ProfissionalDadosPessoais";
import { ProfissionalAcesso } from "@/components/admin/ProfissionalAcesso";
import { ProfissionalPacientes } from "@/components/admin/ProfissionalPacientes";
import { ProfissionalAssinatura } from "@/components/admin/ProfissionalAssinatura";
import { ProfissionalCartoes } from "@/components/admin/ProfissionalCartoes";
import { ProfissionalPagamentos } from "@/components/admin/ProfissionalPagamentos";
import { ProfissionalUsuariosConectados } from "@/components/admin/ProfissionalUsuariosConectados";
import { Card } from "@/components/ui/card";

interface Professional {
  id: string;
  full_name: string;
  email: string;
  cpf: string;
  phone: string;
  avatar_url: string | null;
  current_plan_code: string | null;
  health_area: string | null;
  created_at: string;
}

export default function ProfissionalDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [professional, setProfessional] = useState<Professional | null>(null);

  useEffect(() => {
    loadProfessional();
  }, [id]);

  const loadProfessional = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-professional-details', {
        body: { professionalId: id }
      });

      if (error) {
        console.error("Error loading professional:", error);
        toast.error("Erro ao carregar dados do profissional");
        navigate("/admin/cadastros/profissionais");
        return;
      }

      if (!data) {
        toast.error("Profissional não encontrado");
        navigate("/admin/cadastros/profissionais");
        return;
      }

      setProfessional({
        id: data.id,
        full_name: data.full_name,
        email: data.email,
        cpf: data.cpf,
        phone: data.phone,
        avatar_url: data.avatar_url,
        current_plan_code: data.current_plan_code,
        health_area: data.health_area,
        created_at: data.created_at,
      });
    } catch (error) {
      console.error("Error loading professional:", error);
      toast.error("Erro ao carregar dados do profissional");
      navigate("/admin/cadastros/profissionais");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!professional) {
    return <div>Profissional não encontrado</div>;
  }

  return (
    <div className="space-y-6">
      {/* AdminBreadcrumbs é automático baseado na rota */}

      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin/cadastros/profissionais")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>

      <div className="bg-gradient-to-r from-primary/10 to-cyan-600/10 p-6 rounded-xl border">
        <div className="flex items-start gap-6">
          {professional.avatar_url ? (
            <img src={professional.avatar_url} alt={professional.full_name} className="w-24 h-24 rounded-full" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center text-3xl font-bold text-primary">
              {professional.full_name.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">{professional.full_name}</h1>
              {professional.health_area && (
                <Badge variant="secondary" className="text-sm">
                  {professional.health_area}
                </Badge>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">CPF:</span> {professional.cpf || "N/A"}
              </div>
              <div>
                <span className="text-muted-foreground">Email:</span> {professional.email}
              </div>
              <div>
                <span className="text-muted-foreground">Telefone:</span> {professional.phone || "N/A"}
              </div>
              <div>
                <span className="text-muted-foreground">Plano:</span> {professional.current_plan_code || "Nenhum"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="dados">Dados</TabsTrigger>
          <TabsTrigger value="acesso">Acesso</TabsTrigger>
          <TabsTrigger value="pacientes">Pacientes</TabsTrigger>
          <TabsTrigger value="assinatura">Assinatura</TabsTrigger>
          <TabsTrigger value="cartoes">Cartões</TabsTrigger>
          <TabsTrigger value="pagamentos">Pagamentos</TabsTrigger>
          <TabsTrigger value="usuarios">Usuários</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <ProfissionalDashboard professionalId={id!} />
        </TabsContent>

        <TabsContent value="dados" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Dados Pessoais</h3>
            <ProfissionalDadosPessoais professionalId={id!} />
          </Card>
        </TabsContent>

        <TabsContent value="acesso" className="mt-6">
          <ProfissionalAcesso professionalId={id!} email={professional.email} />
        </TabsContent>

        <TabsContent value="pacientes" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Pacientes</h3>
            <ProfissionalPacientes professionalId={id!} professionalName={professional.full_name} />
          </Card>
        </TabsContent>

        <TabsContent value="assinatura" className="mt-6">
          <ProfissionalAssinatura professionalId={id!} professionalName={professional.full_name} />
        </TabsContent>

        <TabsContent value="cartoes" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Cartões</h3>
            <ProfissionalCartoes professionalId={id!} />
          </Card>
        </TabsContent>

        <TabsContent value="pagamentos" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Pagamentos</h3>
            <ProfissionalPagamentos professionalId={id!} />
          </Card>
        </TabsContent>

        <TabsContent value="usuarios" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Usuários Conectados</h3>
            <ProfissionalUsuariosConectados professionalId={id!} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
